import api from './api';

export interface MatchGame {
  _id: string;
  title: string;
  description?: string;
  type: 'deck-based' | 'custom' | 'ai-generated';
  deckId?: string;
  deckTitle?: string;
  pairsCount: number;
  pairs: MatchPair[];
  tags: string[];
  attemptsCount: number;
  lastAttempted?: string;
  averageTime: number;
  bestTime: number;
  isPublic: boolean;
  reviewCount: number;
  nextReview: string;
  easeFactor: number;
  interval: number;
  createdAt: string;
  updatedAt: string;
}

export interface MatchGameListItem {
  _id: string;
  title: string;
  description?: string;
  type: 'deck-based' | 'custom' | 'ai-generated';
  deckId?: string;
  deckTitle?: string;
  pairsCount: number;
  tags: string[];
  attemptsCount: number;
  lastAttempted?: string;
  averageTime: number;
  bestTime: number;
  reviewCount: number;
  nextReview: string;
  easeFactor: number;
  interval: number;
  createdAt: string;
  updatedAt: string;
}

export interface MatchPair {
  id: string;
  front: string;
  back: string;
  cardId?: string;
}

export interface CreateMatchData {
  title: string;
  description?: string;
  type: 'deck-based' | 'custom' | 'ai-generated';
  deckId?: string;
  pairsCount?: number;
  pairs?: {
    front: string;
    back: string;
  }[];
  topic?: string;
  difficulty?: 'easy' | 'medium' | 'hard';
  tags?: string[];
  isPublic?: boolean;
}

export interface UpdateMatchData {
  title?: string;
  description?: string;
  pairs?: {
    front: string;
    back: string;
  }[];
  tags?: string[];
  isPublic?: boolean;
}

export interface MatchAttempt {
  _id: string;
  matchGameId: string;
  userId: string;
  timeSpent: number;
  movesCount: number;
  isCompleted: boolean;
  startedAt: string;
  completedAt?: string;
  pairMatches: {
    pairId: string;
    matchedAt: string;
    attemptsBeforeMatch: number;
  }[];
}

export interface MatchResults {
  attempt: MatchAttempt;
  matchGame: {
    _id: string;
    title: string;
    description?: string;
    pairsCount: number;
  };
  summary: {
    timeSpent: number;
    movesCount: number;
    pairsCount: number;
    isNewBestTime: boolean;
    averageTimePerPair: number;
  };
}

export interface MatchStats {
  totalGames: number;
  totalAttempts: number;
  totalPairs?: number;
  averageTime: number;
  bestTime: number;
  totalTimeSpent: number;
  recentAttempts: MatchAttempt[];
}

class MatchService {
  async getMatchGames(): Promise<MatchGameListItem[]> {
    console.log('MatchService: Making API call to /matches');
    try {
      const response = await api.get('/matches');
      console.log('MatchService: API response:', response.data);
      return response.data;
    } catch (error) {
      console.error('MatchService: API call failed:', error);
      throw error;
    }
  }

  async getMatchGame(id: string): Promise<MatchGame> {
    const response = await api.get(`/matches/${id}`);
    return response.data;
  }

  async createMatch(matchData: CreateMatchData): Promise<MatchGame> {
    const response = await api.post('/matches', matchData);
    return response.data;
  }

  async updateMatch(id: string, matchData: UpdateMatchData): Promise<MatchGame> {
    const response = await api.patch(`/matches/${id}`, matchData);
    return response.data;
  }

  async deleteMatch(id: string): Promise<void> {
    await api.delete(`/matches/${id}`);
  }

  async startMatchAttempt(matchGameId: string): Promise<MatchAttempt> {
    const response = await api.post('/matches/attempts/start', { matchGameId });
    return response.data;
  }

  async submitMatchAttempt(
    attemptId: string,
    timeSpent: number,
    movesCount: number,
    pairMatches: {
      pairId: string;
      matchedAt: string;
      attemptsBeforeMatch: number;
    }[]
  ): Promise<MatchResults> {
    const response = await api.post('/matches/attempts/submit', {
      attemptId,
      timeSpent,
      movesCount,
      pairMatches
    });
    return response.data;
  }

  async getMatchStats(): Promise<MatchStats> {
    const response = await api.get('/matches/stats');
    return response.data;
  }

  async getAttemptHistory(limit: number = 365): Promise<MatchAttempt[]> {
    const response = await api.get('/matches/attempts/history', {
      params: { limit }
    });
    return response.data;
  }

  async getMatchAttempts(matchGameId: string): Promise<MatchAttempt[]> {
    const response = await api.get(`/matches/${matchGameId}/attempts`);
    return response.data;
  }

  async requestAIMatch(aiRequest: {
    topic: string;
    pairsCount: number;
    difficulty: 'easy' | 'medium' | 'hard';
    language: 'portuguese' | 'english' | 'spanish';
  }): Promise<{ success: boolean; message: string; matchGameId?: string }> {
    const response = await api.post('/matches/ai/generate', aiRequest);
    return response.data;
  }

  async createMatchFromFile(formData: FormData): Promise<{ success: boolean; message: string; matchGameId: string }> {
    const response = await api.post('/matches/ai/generate-from-file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }
}

export default new MatchService();
